// SAME ROAD, TWO ENGINES (Morgan, Aug 8, Avigliano Umbro: a 0.5 mi hop
// showed "Pleasant · 4 min" and "Fast · 2 min" — identical street, two
// routing engines, two ETA models. "It is acting like these are two
// different routes ... they are not.")
//
// Rule 3 says drop a card only for IDENTICAL routes — not similarity
// percentages. This helper decides identity GEOMETRICALLY, scale-aware:
// same length (within engine noise) and the polylines lie on top of each
// other (mean deviation a few tens of meters). Duration is deliberately
// NOT consulted: on identical geometry a duration difference is model
// disagreement, never a different road. The bucket-jaccard test in
// scoring stays for big routes; this exists because a 0.5 mi trip has too
// few buckets for jaccard to mean anything.
import type { LngLat } from "@/utils/polyline";
import { nearestPointOnPolyline } from "@/utils/polyline";

interface TwinRoute {
  coordinates: LngLat[];
  distance: number; // meters
}

/** Sample n points at even fractions of a polyline (by index — good
 *  enough for identity checks; both engines densify similarly). */
function samples(coords: LngLat[], n: number): LngLat[] {
  if (coords.length === 0) return [];
  const out: LngLat[] = [];
  for (let i = 0; i < n; i++) {
    const idx = Math.min(coords.length - 1, Math.round((i / (n - 1)) * (coords.length - 1)));
    out.push(coords[idx]);
  }
  return out;
}

/** Mean distance from a's sample points to b's polyline SEGMENTS (not its
 *  sampled points — on a 200 km road, sparse points leave km-wide gaps
 *  between them and everything looks "far"). */
function meanDeviationM(a: LngLat[], b: LngLat[]): number {
  const pa = samples(a, 7);
  if (pa.length === 0 || b.length < 2) return Infinity;
  let sum = 0;
  for (const p of pa) {
    sum += nearestPointOnPolyline(p, b).distance;
  }
  return sum / pa.length;
}

/** True when two routes are the SAME ROAD — identical geometry within
 *  engine-encoding noise. Scale-aware: tight on short trips (~40 m),
 *  capped at 150 m on long ones (same corridor = same road). */
export function routesAreSameRoad(a: TwinRoute, b: TwinRoute): boolean {
  const L = Math.max(a.distance, b.distance);
  if (L <= 0) return false;
  if (Math.abs(a.distance - b.distance) > Math.max(120, 0.08 * L)) return false;
  const tolerance = Math.min(150, Math.max(40, 0.05 * L));
  // SYMMETRIC (Morgan, Aug 23). meanDeviationM samples ONE route and measures
  // to the other's polyline, so it answers "does A lie on B" — not "are A and
  // B the same road". Those differ exactly where it matters: if B contains all
  // of A plus a detour, every sample of A sits on B and the deviation reads
  // ~0, so a route with an extra loop in it reads as identical to the route
  // without one.
  //
  // Caught on production Pleasantville: a 4.54 mi route and a 5.11 mi route
  // measured 5 m apart on 60 samples. Only the length guard above stopped that
  // pair being called the same road, which made a threshold the sole thing
  // standing between us and a wrong collapse.
  //
  // Both directions now have to agree. The detour shows up the moment you
  // sample the LONGER route: its points out on the spur are far from the
  // shorter line, so B->A is large even when A->B is ~0. Thresholds are
  // unchanged — this is a fix to the measurement, not a tuning pass.
  return (
    meanDeviationM(a.coordinates, b.coordinates) < tolerance &&
    meanDeviationM(b.coordinates, a.coordinates) < tolerance
  );
}

/**
 * DIAGNOSTIC ONLY — returns the numbers `routesAreSameRoad` actually decides
 * on, so a caller can print them instead of guessing (Morgan, Aug 23). It
 * calls the same helpers with the same constants; nothing here is a second
 * implementation of the rule, and nothing here changes behaviour.
 *
 * Also reports a DENSE deviation using 60 samples rather than 7. The rule's
 * verdict comes from 7 points averaged, which is coarse enough that a real
 * divergence over part of the route can be averaged away; the dense figure and
 * the worst single sample say whether that is happening.
 */
export function sameRoadDiagnostics(a: TwinRoute, b: TwinRoute): {
  distanceDeltaM: number; distanceLimitM: number;
  meanDeviation7M: number; meanDeviation60M: number; worstSampleM: number;
  toleranceM: number; same: boolean;
} {
  const L = Math.max(a.distance, b.distance);
  const distanceDeltaM = Math.abs(a.distance - b.distance);
  const distanceLimitM = Math.max(120, 0.08 * L);
  const toleranceM = Math.min(150, Math.max(40, 0.05 * L));
  const denseDev = (from: LngLat[], to: LngLat[]) => {
    const pts = samples(from, 60);
    let s = 0, w = 0;
    for (const p of pts) {
      const d = nearestPointOnPolyline(p, to).distance;
      s += d;
      if (d > w) w = d;
    }
    return { mean: pts.length ? s / pts.length : Infinity, worst: w };
  };
  const ab = denseDev(a.coordinates, b.coordinates);
  const ba = denseDev(b.coordinates, a.coordinates);
  return {
    distanceDeltaM,
    distanceLimitM,
    meanDeviation7M: Math.max(
      meanDeviationM(a.coordinates, b.coordinates),
      meanDeviationM(b.coordinates, a.coordinates),
    ),
    meanDeviation60M: Math.max(ab.mean, ba.mean),
    worstSampleM: Math.max(ab.worst, ba.worst),
    toleranceM,
    same: routesAreSameRoad(a, b),
  };
}
